<script lang="ts">
  import CartWidgetGroupV2 from "@retailer/svelte/CartWidget/v2/CartWidgetGroupV2.svelte";
  import PickupLocationModal from "@retailer/svelte/Cart/Page/PickupOrDelivery/PickupLocationModal.svelte";
  import CartWidgetNotice from "@retailer/svelte/CartWidget/v2/CartWidgetNotice.svelte";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";
  import { pickupWarehousesStore } from "@retailer/svelte/stores/PickupWarehousesStore";
  import CartStore from "@retailer/svelte/stores/CartStore";
  import {
    sendCartTrackingEvents,
    sendClickTracking,
  } from "@retailer/svelte/CartWidget/v2/Services/CartEventTracking";
  import { CartWidgetTargetObjectBase } from "@shared/services/event_tracking/entities";
  import Flash from "@shared/services/flashv2";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import * as PickupWarehousesService from "@retailer/services/pickup_warehouses_service";
  import RetailerFulfillmentMethodsStore from "@retailer/svelte/stores/RetailerFulfillmentMethodsStore";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import FulfillmentMethodPicker from "@retailer/svelte/Shared/FulfillmentMethodPicker.svelte";
  import {
    shouldUseRetailerFulfillmentMethodForDistributor,
    hideFulfillmentMethodPickerForDistributor,
  } from "@retailer/services/fulfillment_methods_service";

  import type { Notice } from "@retailer/svelte/CartWidget/v2/Services/CartItemRequestService";
  import type { BaseEntityClass } from "@shared/services/event_tracking/entities/base_entity_class";
  import type { CartProduct, Product } from "@shared/types/ProductLine";
  import type { DistributorCart } from "@shared/types/Distributor";
  import type { RetailerFulfillmentMethod } from "@shared/types/RetailerFulfillmentMethod";

  export let inventoryId: number;
  export let product: CartProduct | Product;
  export let products: Product[] = [];
  export let distributor: DistributorCart;
  export let trackingEntities: BaseEntityClass[] = [];
  export let loaded: boolean = false;

  const targetObjectTypeBase = CartWidgetTargetObjectBase.CartRecommendationProduct;
  const { trackClick } = EventTracking;

  const trackCartChangeEvent = event => {
    sendCartTrackingEvents(event, trackingEntities, inventoryId);
  };

  const trackCartInteractionEvent = event => {
    sendClickTracking(targetObjectTypeBase, event, trackingEntities);
  };

  const showFlashMessage = event => {
    if (event.detail.eventName === "cart_add") {
      Flash.now({
        rawHtml: true,
        type: "success",
        message: `
        <span class="fizz-text-strong">
          ${product.product_line.name}
        </span>
        <span> was added to cart.</span>
      `,
      });
    }
  };

  const onUpdateQuantity = event => {
    trackCartInteractionEvent(event);
    showFlashMessage(event);
  };

  let showPickupSelectionModal = false;
  const onPickupClick = async () => {
    trackClick("check_pickup_availability", trackingEntities);
    const productId = product.product_id || product.id;
    await PickupWarehousesService.get(productId, distributor.id);
    showPickupSelectionModal = true;
  };

  const onPickupSelection = async selectedPickupLocation => {
    const quantityToAdd = 1;

    const unitQuantity = cartWidgetItem.sold_by.includes("units") ? quantityToAdd : 0;
    const caseQuantity = unitQuantity ? 0 : quantityToAdd;

    await InventoryItemStore.addToCart(inventoryId, {
      inventory_id: inventoryId,
      unit_quantity: unitQuantity,
      case_quantity: caseQuantity,
      warehouse_id: selectedPickupLocation.id,
    });
    await CartStore.load();
    closeModal();
  };
  const closeModal = () => {
    showPickupSelectionModal = false;
  };

  const hasFullfillmentMethodsRollout = stagedRolloutCheck("marketplace__fulfillment_methods");
  let showFulfillmentMethodsDrawer: boolean = false;

  const openFulfillmentMethodsDrawer = (): void => {
    showFulfillmentMethodsDrawer = !showFulfillmentMethodsDrawer;
  };

  let fulfillmentMethod: RetailerFulfillmentMethod = null;
  let fulfillmentModel: string = null;

  $: cartWidgetItem = $InventoryItemStore?.inventoryItems[inventoryId];
  $: informationalNotices = cartWidgetItem?.notices.filter((notice: Notice) => {
    return notice.level === "info" && notice.message;
  });
  $: notices = cartWidgetItem?.notices.filter(notice => notice.level !== "info" && notice.message);
  $: noticesIncludeErrors = notices?.some(notice => notice.level === "error");
  $: pickUpOptionsAvailable =
    cartWidgetItem?.stock_info?.additional_action?.endsWith("pickup_available");
  $: fulfillmentModel = cartWidgetItem?.distributor_summary?.fulfillment_model;
  $: {
    if (
      hideFulfillmentMethodPickerForDistributor(distributor?.id) &&
      fulfillmentModel === "pickup_only"
    ) {
      fulfillmentMethod = $RetailerFulfillmentMethodsStore[distributor?.id] || null;
    }
  }
  $: {
    if (
      shouldUseRetailerFulfillmentMethodForDistributor(
        distributor?.id,
        distributor?.uses_warehouse_pickup,
        hasFullfillmentMethodsRollout && distributor?.uses_wine_cellar,
      ) &&
      !hideFulfillmentMethodPickerForDistributor(distributor?.id)
    ) {
      fulfillmentMethod = $RetailerFulfillmentMethodsStore[distributor?.id] || null;
      InventoryItemStore.fetchInventoryItem(inventoryId);
    }
  }
  $: showOtherAvailableLocations = pickUpOptionsAvailable && !cartWidgetItem?.fulfillment_summary;
  $: defaultWarehouseId = cartWidgetItem?.distributor_summary?.default_warehouse_id;

  $: needsAccountNumber =
    cartWidgetItem?.stock_info?.additional_action?.endsWith("connect_account");
</script>

<style>
  .container-div {
    display: flex;
    flex-direction: column;
  }

  @media (min-width: 700px) {
    .desktop-width {
      max-width: 250px;
    }
  }
</style>

{#if loaded && cartWidgetItem}
  <div class="fizz-flow-8 container-div">
    {#each notices as notice}
      <CartWidgetNotice {notice} />
    {/each}
    <CartWidgetGroupV2
      widgetItem={cartWidgetItem}
      displayAsRow={true}
      {fulfillmentMethod}
      fulfillmentWarehouseId={fulfillmentMethod?.warehouse_id || defaultWarehouseId}
      on:cartWidgetChange={trackCartChangeEvent}
      on:updateQuantity={onUpdateQuantity}
    />
    {#if showOtherAvailableLocations}
      <div class="fizz-stack-8">
        {#if hasFullfillmentMethodsRollout && cartWidgetItem?.distributor_summary?.fulfillment_model === "pickup_only"}
          <button
            on:click|preventDefault={cartWidgetItem?.distributor_summary?.uses_wine_cellar
              ? openFulfillmentMethodsDrawer
              : onPickupClick}
            class="fizz-button fizz-full-width desktop-width"
          >
            Check other locations
          </button>
        {:else if !hasFullfillmentMethodsRollout || (hasFullfillmentMethodsRollout && cartWidgetItem?.distributor_summary?.fulfillment_model === "pickup_and_delivery" && !needsAccountNumber)}
          <button
            on:click|preventDefault={hasFullfillmentMethodsRollout &&
            cartWidgetItem?.distributor_summary?.uses_wine_cellar
              ? openFulfillmentMethodsDrawer
              : onPickupClick}
            class="fizz-button fizz-full-width desktop-width"
          >
            View more availability
          </button>
        {/if}
      </div>
      <FulfillmentMethodPicker
        bind:open={showFulfillmentMethodsDrawer}
        {distributor}
        {products}
        skus={[cartWidgetItem.sku]}
        forProductLine={false}
      />
    {/if}
    {#if noticesIncludeErrors}
      <a
        class="fizz-button fizz-full-width desktop-width"
        href="/products/{product.product_line.id}"
      >
        See other sizes
      </a>
    {/if}
    {#each informationalNotices as notice}
      <CartWidgetNotice {notice} />
    {/each}
  </div>
{/if}

{#if showPickupSelectionModal && $pickupWarehousesStore.loaded}
  <PickupLocationModal
    selectedPickupLocationId={null}
    pickupOptions={$pickupWarehousesStore.pickupWarehouses}
    {distributor}
    onCancel={closeModal}
    {onPickupSelection}
    shouldShowLowStock={pickUpOptionsAvailable}
  />
{/if}
